import { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from "react";
import { PlaybackSource } from "../utils/playbackSourcePresentation";
import {
  emptySourcePreference,
  clearAllSourcePreferences,
  rankPlaybackSources,
  readSourceHealth,
  RankedPlaybackSource,
  readSourcePreference,
  saveSourcePreference,
  saveSourceOutcome,
  SourcePreference,
  StoredSourceHealth,
  getPlaybackSourceFingerprint,
} from "./sourceRanking";
import { getPlaybackSourceCatalogIdentity, mergePlaybackSources } from "./sourceCatalog";

type PlayerSourcesInput = {
  itemSources?: PlaybackSource[];
  probedSources?: PlaybackSource[];
  selectedSourceId: string;
  setSelectedSourceId: Dispatch<SetStateAction<string>>;
  explicitSourceId?: string;
  preferenceScope: string;
  preferredAudioLanguage: string;
  allowAutomaticPinnedSelection: boolean;
};

export function usePlayerSources({
  itemSources,
  probedSources,
  selectedSourceId,
  setSelectedSourceId,
  explicitSourceId,
  preferenceScope,
  preferredAudioLanguage,
  allowAutomaticPinnedSelection,
}: PlayerSourcesInput) {
  const [preference, setPreference] = useState<SourcePreference>(emptySourcePreference);
  const [loadedScope, setLoadedScope] = useState("");
  const [health, setHealth] = useState<StoredSourceHealth>({});
  const appliedScope = useRef("");
  const sources = useMemo(
    () => mergePlaybackSources(itemSources, probedSources),
    [itemSources, probedSources],
  );
  const preprobedSourceIds = useMemo(
    () => new Set((probedSources ?? []).filter((entry) => (entry.MediaStreams?.length ?? 0) > 0).map((entry) => entry.Id).filter((id): id is string => Boolean(id))),
    [probedSources],
  );
  const rankedSources = useMemo(
    () => rankPlaybackSources(sources, preference, preferredAudioLanguage, health, preprobedSourceIds),
    [health, preference, preferredAudioLanguage, preprobedSourceIds, sources],
  );
  const preferenceReady = Boolean(preferenceScope && loadedScope === preferenceScope);
  const selectedInputSource = selectedSourceId
    ? [...(itemSources ?? []), ...(probedSources ?? [])]
      .find((entry) => entry.Id === selectedSourceId)
    : undefined;
  const selectedInputIdentity = selectedInputSource
    ? getPlaybackSourceCatalogIdentity(selectedInputSource)
    : "";
  const selectedSource = sources.find((entry) => entry.Id === selectedSourceId) ??
    (selectedInputIdentity
      ? sources.find((entry) => getPlaybackSourceCatalogIdentity(entry) === selectedInputIdentity)
      : undefined);
  const explicitSource = explicitSourceId
    ? sources.find((entry) => entry.Id === explicitSourceId)
    : undefined;
  const probedExplicitFallback = explicitSourceId && !explicitSource
    ? probedSources?.[0]
    : undefined;
  const source = selectedSource ?? explicitSource ?? probedExplicitFallback ??
    rankedSources.find((entry) => entry.pinned && !entry.ignored)?.source ??
    rankedSources.find((entry) => !entry.ignored)?.source ??
    sources[0];

  useEffect(() => {
    let active = true;
    setLoadedScope("");
    appliedScope.current = "";
    void readSourcePreference(preferenceScope).then((value) => {
      if (!active) return;
      setPreference(value);
      setLoadedScope(preferenceScope);
    });
    return () => { active = false; };
  }, [preferenceScope]);
  useEffect(() => {
    let active = true;
    void readSourceHealth().then((value) => {
      if (active) setHealth(value);
    });
    return () => { active = false; };
  }, []);
  useEffect(() => {
    if (
      selectedSourceId &&
      selectedSource?.Id &&
      selectedSource.Id !== selectedSourceId
    ) {
      setSelectedSourceId(selectedSource.Id);
    }
  }, [selectedSource?.Id, selectedSourceId, setSelectedSourceId]);
  useEffect(() => {
    if (
      !selectedSourceId &&
      source?.Id &&
      (preferenceReady || !preferenceScope)
    ) setSelectedSourceId(source.Id);
  }, [
    preferenceReady,
    preferenceScope,
    selectedSourceId,
    setSelectedSourceId,
    source?.Id,
  ]);
  useEffect(() => {
    if (
      !preferenceReady ||
      !preferenceScope ||
      explicitSourceId ||
      selectedSourceId ||
      !allowAutomaticPinnedSelection
    ) return;
    if (appliedScope.current === preferenceScope) return;
    appliedScope.current = preferenceScope;
    const pinned = rankedSources.find((entry) => entry.pinned && !entry.ignored)?.source;
    if (pinned?.Id && pinned.Id !== source?.Id) setSelectedSourceId(pinned.Id);
  }, [allowAutomaticPinnedSelection, explicitSourceId, preferenceReady, preferenceScope, rankedSources, selectedSourceId, setSelectedSourceId, source?.Id]);

  const commitPreference = (next: SourcePreference) => {
    setPreference(next);
    if (preferenceScope) void saveSourcePreference(preferenceScope, next).catch(() => undefined);
  };
  const togglePinnedSource = (entry: RankedPlaybackSource) => {
    commitPreference({
      ...preference,
      preferredFingerprint: entry.pinned ? undefined : entry.fingerprint,
      ignoredFingerprints: preference.ignoredFingerprints.filter((value) => value !== entry.fingerprint),
    });
  };
  const toggleIgnoredSource = (entry: RankedPlaybackSource) => {
    const ignored = preference.ignoredFingerprints.includes(entry.fingerprint);
    commitPreference({
      preferredFingerprint: entry.pinned && !ignored ? undefined : preference.preferredFingerprint,
      ignoredFingerprints: ignored
        ? preference.ignoredFingerprints.filter((value) => value !== entry.fingerprint)
        : [...preference.ignoredFingerprints, entry.fingerprint],
    });
  };

  const clearSourceScoring = () => {
    setPreference(emptySourcePreference);
    setHealth({});
    appliedScope.current = "";
    void clearAllSourcePreferences().catch(() => undefined);
  };
  const recordSourceOutcome = (
    playedSource: PlaybackSource | undefined,
    outcome: "success" | "failure",
  ) => {
    if (!playedSource) return;
    const fingerprint = getPlaybackSourceFingerprint(playedSource);
    void saveSourceOutcome(fingerprint, outcome)
      .then(setHealth)
      .catch(() => undefined);
  };

  return {
    sources,
    rankedSources,
    source,
    preferenceReady,
    togglePinnedSource,
    toggleIgnoredSource,
    clearSourceScoring,
    recordSourceOutcome,
  };
}
